import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowsClockwise, Broom, ClockCounterClockwise, Copy, ListChecks, ShieldWarning, UsersThree, Vault, CaretRight, Snowflake, ArrowSquareOut,
} from '@phosphor-icons/react';
import {
  NETWORKS, getAsset,
  type AdminActionDTO, type AdminOverviewDTO, type AdminReservesDTO, type AdminSweepDTO, type AdminUserDetailDTO, type AdminUserDTO,
  type AdminWithdrawalDTO, type AssetCode, type NetworkId,
} from '@onyx/shared';
import { api, ApiError } from '../lib/api';
import { useLang, useT, errorText } from '../lib/i18n';
import { cleanAmountInput, cryptoParts, displayDigits, formatCrypto, formatDateTime, shortAddress } from '../lib/format';
import { navigate } from '../lib/router';
import { copyText, haptic, openExternal } from '../lib/telegram';
import { AssetChip } from '../components/AssetChip';
import { NetworkMark } from '../components/NetworkMark';
import { Avatar, Money, ErrorState, Facts, Field, Notice, Row, Screen, ScreenHeader, Section, Segmented, Skeleton, Slab, toast } from '../components/ui';
import { AssetSwitch } from './Send';
import { ActivityRow } from './History';

/* ---------- data ---------- */

const adminKey = (...k: unknown[]) => ['admin', ...k];
const useAdmin = <T,>(path: string, key: unknown[], opts: { refetchInterval?: number } = {}) =>
  useQuery({ queryKey: adminKey(...key), queryFn: () => api<T>(`/admin${path}`), ...opts });

function useAdminAction<V>(fn: (v: V) => Promise<unknown>, done?: string) {
  const qc = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      haptic.success();
      if (done) toast.good(done);
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => {
      haptic.error();
      toast.bad(e instanceof ApiError ? e.message || errorText(t, e.code) : errorText(t, undefined));
    },
  });
}

const amountText = (amount: string, asset: string, lang: 'ru' | 'en') =>
  `${formatCrypto(amount, displayDigits(Math.abs(Number(amount)), !!getAsset(asset)?.stable), lang)} ${asset}`;

function Copyable({ value, label }: { value: string; label?: string }) {
  return (
    <button type="button" className="copyable num" onClick={() => void copyText(value).then(() => toast.good('Скопировано'))}>
      {label ?? shortAddress(value)} <Copy size={14} aria-hidden="true" />
    </button>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone =
    ['review', 'pending', 'processing', 'gas_sent', 'sent', 'deferred'].includes(status) ? 'warn' : ['failed', 'cancelled'].includes(status) ? 'bad' : 'good';
  const label: Record<string, string> = {
    review: 'На проверке', pending: 'В очереди', processing: 'Подписан', sent: 'Отправлен', completed: 'Выполнен', confirmed: 'Подтверждён',
    failed: 'Ошибка', cancelled: 'Отменён', gas_sent: 'Газ отправлен', deferred: 'Отложен',
  };
  return <span className={`admin-chip admin-chip--${tone}`}>{label[status] ?? status}</span>;
}

/* ---------- overview ---------- */

export function AdminOverview() {
  const lang = useLang();
  const q = useAdmin<AdminOverviewDTO>('/overview', ['overview'], { refetchInterval: 20_000 });
  const d = q.data;
  const nav = [
    { to: '/admin/withdrawals', Icon: ListChecks, title: 'Выводы', sub: d ? `На проверке: ${d.withdrawals.review} · в работе: ${d.withdrawals.pending}` : '', alert: !!d?.withdrawals.review },
    { to: '/admin/sweeps', Icon: Broom, title: 'Сбор депозитов', sub: d ? `Активных: ${d.sweeps.active} · ошибок: ${d.sweeps.failed} · отложено: ${d.sweeps.deferred}` : '', alert: !!d?.sweeps.failed },
    { to: '/admin/reserves', Icon: Vault, title: 'Резервы и горячие кошельки', sub: 'Балансы в блокчейне против обязательств', alert: false },
    { to: '/admin/users', Icon: UsersThree, title: 'Пользователи', sub: 'Поиск, заморозка, корректировки', alert: false },
    { to: '/admin/log', Icon: ClockCounterClockwise, title: 'Журнал действий', sub: 'Кто, что и когда сделал', alert: false },
  ];
  return (
    <Screen className="admin">
      <ScreenHeader title="Админка" />
      {q.isError ? (
        <ErrorState text="Нет доступа или сервер недоступен" onRetry={() => void q.refetch()} />
      ) : (
        <>
          {d?.testnet && <Notice tone="warn" icon={<ShieldWarning size={18} weight="fill" />}>Тестовая сеть: суммы не настоящие.</Notice>}
          <div className="admin-stats">
            {[
              { label: 'Пользователи', value: d?.users.total, sub: d ? `+${d.users.today} за сутки` : '' },
              { label: 'Депозиты 24 ч', value: d?.deposits24h, sub: 'зачислено' },
              { label: 'Заморожено', value: d?.users.frozen, sub: 'аккаунтов' },
            ].map((s) => (
              <div key={s.label} className="card admin-stat">
                <span className="admin-stat__label">{s.label}</span>
                <span className="admin-stat__value num">{s.value ?? '—'}</span>
                <span className="admin-stat__sub">{s.sub}</span>
              </div>
            ))}
          </div>

          <div className="admin-nav">
            {nav.map(({ to, Icon, title, sub, alert }) => (
              <button key={to} type="button" className={`card soft-row${alert ? ' soft-row--alert' : ''}`} onClick={() => navigate(to)}>
                <span className="soft-row__icon">
                  <Icon size={22} weight="fill" aria-hidden="true" />
                </span>
                <span className="soft-row__text">
                  <span className="soft-row__title">{title}</span>
                  <span className="soft-row__sub">{sub}</span>
                </span>
                <CaretRight size={20} weight="bold" className="soft-row__chev" aria-hidden="true" />
              </button>
            ))}
          </div>

          <Section title="Обязательства перед пользователями">
            {q.isPending ? (
              <Skeleton w="100%" h={120} r={24} />
            ) : (
              <div className="rows">
                {d!.assets
                  .filter((a) => a.liabilities !== '0' || a.treasury !== '0' || a.unswept !== '0')
                  .map((a) => (
                    <Row
                      key={a.asset}
                      leading={<AssetChip asset={a.asset} size={36} />}
                      title={a.asset}
                      subtitle={
                        <span className="num">
                          Казна {amountText(a.treasury, a.asset, lang)} · несобрано {amountText(a.unswept, a.asset, lang)}
                          {a.escrow !== '0' && ` · в чеках ${amountText(a.escrow, a.asset, lang)}`}
                        </span>
                      }
                      wrapSub
                      trailing={<Money parts={cryptoParts(a.liabilities, displayDigits(Number(a.liabilities), !!getAsset(a.asset)?.stable), lang)} unit={a.asset} size="md" />}
                      trailingSub={a.locked !== '0' ? <span className="num is-warn">{amountText(a.locked, a.asset, lang)} в выводах</span> : 'должны'}
                    />
                  ))}
              </div>
            )}
          </Section>
        </>
      )}
    </Screen>
  );
}

/* ---------- withdrawals ---------- */

const W_FILTERS = [
  { id: 'review', label: 'На проверке' },
  { id: 'pending,processing,sent', label: 'В работе' },
  { id: 'failed', label: 'Ошибки' },
  { id: '', label: 'Все' },
];

function WithdrawalCard({ w }: { w: AdminWithdrawalDTO }) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const approve = useAdminAction(() => api(`/admin/withdrawals/${w.id}/approve`, { body: {} }), 'Вывод отправлен в очередь');
  const reject = useAdminAction(() => api(`/admin/withdrawals/${w.id}/reject`, { body: { reason } }), 'Отклонено, средства возвращены');
  return (
    <div className="card admin-item">
      <button type="button" className="admin-item__head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <NetworkMark network={w.network} size={36} />
        <span className="admin-item__main">
          <span className="admin-item__title num">{amountText(w.amount, w.asset, lang)}</span>
          <span className="admin-item__sub">
            {w.user.username ? `@${w.user.username}` : w.user.firstName} · {NETWORKS[w.network].name} · {formatDateTime(w.createdAt, lang)}
          </span>
        </span>
        <StatusChip status={w.status} />
      </button>
      {open && (
        <div className="admin-item__body">
          <div className="review__address">
            <span className="review__label">Адрес получателя</span>
            <span className="review__value">
              {w.address}
            </span>
          </div>
          <Facts
            items={[
              w.memo && { label: 'Memo', value: w.memo },
              { label: 'Комиссия', value: <span className="num">{amountText(w.fee, w.asset, lang)}</span> },
              { label: 'Пользователь', value: <button type="button" className="link-btn" onClick={() => navigate(`/admin/users/${w.user.id}`)}>{w.user.username ? `@${w.user.username}` : w.user.firstName} · {w.user.tgId}</button> },
              w.error && { label: 'Ошибка', value: <span className="is-bad">{w.error}</span> },
              { label: 'ID', value: <Copyable value={w.id} label={w.id.slice(0, 10)} /> },
            ]}
          />
          {w.explorerUrl && (
            <Slab tone="quiet" icon={<ArrowSquareOut size={18} />} onClick={() => openExternal(w.explorerUrl!)}>
              Открыть в обозревателе
            </Slab>
          )}
          {w.status === 'review' && (
            <>
              {w.error && <Notice tone="warn">Перед одобрением проверьте в обозревателе, что прежняя транзакция не попала в сеть: одобрение подписывает вывод заново.</Notice>}
              <Slab loading={approve.isPending} onClick={() => approve.mutate(undefined)}>
                Одобрить и отправить
              </Slab>
              <Field label="Причина отклонения" htmlFor={`rj-${w.id}`}>
                <input id={`rj-${w.id}`} className="input" value={reason} placeholder="Видна в журнале" onChange={(e) => setReason(e.target.value)} />
              </Field>
              <Slab tone="danger" disabled={reason.trim().length < 3} loading={reject.isPending} onClick={() => reject.mutate(undefined)}>
                Отклонить и вернуть средства
              </Slab>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminWithdrawals() {
  const [filter, setFilter] = useState('review');
  const q = useAdmin<{ items: AdminWithdrawalDTO[] }>(`/withdrawals${filter ? `?status=${filter}` : ''}`, ['withdrawals', filter], { refetchInterval: 15_000 });
  return (
    <Screen className="admin">
      <ScreenHeader title="Выводы" />
      <Filters value={filter} options={W_FILTERS} onChange={setFilter} />
      {q.isPending ? <Skeleton w="100%" h={80} r={24} /> : q.isError ? <ErrorState text="Не удалось загрузить" onRetry={() => void q.refetch()} /> : q.data.items.length === 0 ? (
        <p className="state">Пусто.</p>
      ) : (
        <div className="admin-list">
          {q.data.items.map((w) => (
            <WithdrawalCard key={w.id} w={w} />
          ))}
        </div>
      )}
    </Screen>
  );
}

function Filters({ value, options, onChange }: { value: string; options: Array<{ id: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <div className="filters" role="tablist">
      {options.map((f) => (
        <button key={f.id} type="button" role="tab" aria-selected={value === f.id} className={`filter${value === f.id ? ' is-on' : ''}`} onClick={() => onChange(f.id)}>
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- sweeps ---------- */

const S_FILTERS = [
  { id: 'pending,gas_sent,sent', label: 'Активные' },
  { id: 'failed', label: 'Ошибки' },
  { id: 'deferred', label: 'Отложены' },
  { id: '', label: 'Все' },
];

export function AdminSweeps() {
  const lang = useLang();
  const [filter, setFilter] = useState('pending,gas_sent,sent');
  const q = useAdmin<{ items: AdminSweepDTO[] }>(`/sweeps${filter ? `?status=${filter}` : ''}`, ['sweeps', filter], { refetchInterval: 15_000 });
  const retry = useAdminAction((id: string) => api(`/admin/sweeps/${id}/retry`, { body: {} }), 'Сбор поставлен в очередь');
  return (
    <Screen className="admin">
      <ScreenHeader title="Сбор депозитов" />
      <Notice>
        Сборщик раз в минуту переносит депозиты с адресов пользователей на горячий кошелёк сети. Для токенов в EVM и TRON он сначала докидывает газ. TON-депозиты сразу приходят на горячий кошелёк, DOGE собирается вручную.
      </Notice>
      <Filters value={filter} options={S_FILTERS} onChange={setFilter} />
      {q.isPending ? <Skeleton w="100%" h={80} r={24} /> : q.isError ? <ErrorState text="Не удалось загрузить" onRetry={() => void q.refetch()} /> : q.data.items.length === 0 ? (
        <p className="state">Пусто.</p>
      ) : (
        <div className="admin-list">
          {q.data.items.map((s) => (
            <div key={s.id} className="card admin-item">
              <div className="admin-item__head">
                <AssetChip asset={s.asset} size={36} />
                <span className="admin-item__main">
                  <span className="admin-item__title num">{s.amount ? amountText(s.amount, s.asset, lang) : s.asset}</span>
                  <span className="admin-item__sub">
                    {NETWORKS[s.network].name} · <Copyable value={s.address} /> · {formatDateTime(s.updatedAt, lang)}
                  </span>
                </span>
                <StatusChip status={s.status} />
              </div>
              {(s.error || s.explorerUrl || ['failed', 'deferred'].includes(s.status)) && (
                <div className="admin-item__body">
                  {s.error && <p className="admin-item__error">{s.error}</p>}
                  <div className="admin-item__actions">
                    {s.explorerUrl && (
                      <Slab tone="quiet" block={false} icon={<ArrowSquareOut size={18} />} onClick={() => openExternal(s.explorerUrl!)}>
                        Транзакция
                      </Slab>
                    )}
                    {['failed', 'deferred'].includes(s.status) && (
                      <Slab tone="quiet" block={false} icon={<ArrowsClockwise size={18} />} loading={retry.isPending} onClick={() => retry.mutate(s.id)}>
                        Повторить
                      </Slab>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}

/* ---------- reserves ---------- */

export function AdminReserves() {
  const lang = useLang();
  const qc = useQueryClient();
  const q = useAdmin<AdminReservesDTO>('/reserves', ['reserves']);
  const wallets = useAdmin<{ items: Array<{ network: NetworkId; name: string; gas: string; address: string }> }>('/hot-wallets', ['hot-wallets']);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(adminKey('reserves'), await api<AdminReservesDTO>('/admin/reserves?force=1'));
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <Screen className="admin">
      <ScreenHeader title="Резервы" />
      <Notice>
        Покрытие — это горячие кошельки плюс ещё не собранные депозиты, делённые на обязательства перед пользователями. Меньше 100% — денег на выплаты не хватает.
      </Notice>
      {q.isPending ? (
        <Skeleton w="100%" h={200} r={24} />
      ) : q.isError ? (
        <ErrorState text="Не удалось прочитать балансы" onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="admin-coverage">
            {q.data.coverage
              .filter((c) => c.liabilities !== '0' || c.held !== '0')
              .map((c) => {
                const pct = c.ratio === null ? null : Math.round(c.ratio * 100);
                const tone = pct === null ? 'none' : !c.complete ? 'warn' : pct < 100 ? 'bad' : 'good';
                return (
                  <div key={c.asset} className={`card admin-cover admin-cover--${tone}`}>
                    <AssetChip asset={c.asset} size={32} />
                    <span className="admin-cover__pct num">{pct === null ? '—' : `${pct}%`}</span>
                    <span className="admin-cover__sub num">
                      {amountText(c.held, c.asset, lang)} / {amountText(c.liabilities, c.asset, lang)}
                    </span>
                    {!c.complete && <span className="admin-cover__note">не все сети прочитаны</span>}
                  </div>
                );
              })}
          </div>
          <Slab tone="quiet" icon={<ArrowsClockwise size={18} />} loading={refreshing} onClick={() => void refresh()}>
            Обновить из блокчейна
          </Slab>
          <Section title="По сетям">
            <div className="rows">
              {q.data.rows.map((r) => (
                <Row
                  key={`${r.network}-${r.asset}`}
                  leading={<NetworkMark network={r.network} size={36} />}
                  title={`${r.asset} · ${NETWORKS[r.network].name}`}
                  subtitle={r.error ? <span className="is-warn">{r.error.includes('not configured') ? 'Контракт не настроен для этой среды' : r.error}</span> : <span className="num">несобрано {amountText(r.unswept, r.asset, lang)}</span>}
                  wrapSub
                  trailing={r.hot === null ? '—' : <span className="num">{amountText(r.hot, r.asset, lang)}</span>}
                  trailingSub="горячий"
                />
              ))}
            </div>
          </Section>
        </>
      )}
      <Section title="Адреса горячих кошельков">
        <Notice>Сюда пополняйте газ ({'ETH, BNB, TRX, SOL…'}) для выводов и для сбора токенов.</Notice>
        <div className="rows">
          {wallets.data?.items.map((w) => (
            <Row key={w.network} leading={<NetworkMark network={w.network} size={36} />} title={w.name} subtitle={`газ: ${w.gas}`} trailing={<Copyable value={w.address} />} />
          ))}
        </div>
      </Section>
    </Screen>
  );
}

/* ---------- users ---------- */

export function AdminUsers() {
  const [q, setQ] = useState('');
  const query = useAdmin<{ items: AdminUserDTO[] }>(`/users?q=${encodeURIComponent(q)}`, ['users', q]);
  return (
    <Screen className="admin">
      <ScreenHeader title="Пользователи" />
      <label className="search">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="@username, имя, Telegram ID или TON memo" aria-label="Поиск" autoComplete="off" />
      </label>
      {query.isPending ? (
        <Skeleton w="100%" h={120} r={24} />
      ) : query.data?.items.length ? (
        <div className="rows">
          {query.data.items.map((u) => (
            <Row
              key={u.id}
              onClick={() => navigate(`/admin/users/${u.id}`)}
              leading={<Avatar user={u} size={40} />}
              title={u.firstName}
              subtitle={`${u.username ? `@${u.username} · ` : ''}${u.tgId}`}
              trailing={u.frozen ? <span className="admin-chip admin-chip--bad">Заморожен</span> : undefined}
              chevron
            />
          ))}
        </div>
      ) : (
        <p className="state">Никого не нашли.</p>
      )}
    </Screen>
  );
}

export function AdminUser({ id }: { id: string }) {
  const lang = useLang();
  const t = useT();
  const q = useAdmin<AdminUserDetailDTO>(`/users/${id}`, ['user', id]);
  const [freezeReason, setFreezeReason] = useState('');
  const [asset, setAsset] = useState<AssetCode>('USDT');
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const freeze = useAdminAction((frozen: boolean) => api(`/admin/users/${id}/freeze`, { body: { frozen, reason: freezeReason } }), 'Готово');
  const adjust = useAdminAction(
    () => api(`/admin/users/${id}/adjust`, { body: { asset, amount: `${direction === 'debit' ? '-' : ''}${amount}`, reason } }),
    'Баланс скорректирован',
  );
  const u = q.data;

  if (q.isPending) return <Screen className="admin"><ScreenHeader title="Пользователь" /><Skeleton w="100%" h={200} r={24} /></Screen>;
  if (q.isError || !u) return <Screen className="admin"><ScreenHeader title="Пользователь" /><ErrorState text="Не найден" /></Screen>;

  return (
    <Screen className="admin">
      <ScreenHeader title={u.firstName} />
      <div className="profile">
        <Avatar user={u} size={56} />
        <div>
          <p className="profile__name">{u.firstName}</p>
          <p className="profile__id num">
            {u.username ? `@${u.username} · ` : ''}ID {u.tgId} · с {formatDateTime(u.createdAt, lang)}
          </p>
        </div>
      </div>
      {u.frozen && (
        <Notice tone="bad" icon={<Snowflake size={18} weight="fill" />}>
          Заморожен: {u.frozenReason}. Получать может, выводить и переводить — нет.
        </Notice>
      )}

      <Section title="Балансы">
        <div className="rows">
          {u.balances
            .filter((b) => b.available !== '0' || b.locked !== '0')
            .map((b) => (
              <Row
                key={b.asset}
                leading={<AssetChip asset={b.asset} size={36} />}
                title={b.asset}
                subtitle={b.locked !== '0' ? <span className="is-warn num">в обработке {amountText(b.locked, b.asset, lang)}</span> : undefined}
                trailing={<span className="num">{amountText(b.available, b.asset, lang)}</span>}
              />
            ))}
        </div>
      </Section>

      <Section title="Адреса пополнения">
        <div className="rows">
          {u.addresses.map((a) => (
            <Row key={a.family} title={a.family.toUpperCase()} trailing={<Copyable value={a.address} />} />
          ))}
          <Row title="TON memo" trailing={<Copyable value={u.tonMemo} label={u.tonMemo} />} />
        </div>
      </Section>

      <Section title={u.frozen ? 'Разморозка' : 'Заморозка'}>
        <Field label="Причина (видна в журнале)" htmlFor="fz-reason">
          <input id="fz-reason" className="input" value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} />
        </Field>
        <Slab tone="danger" disabled={freezeReason.trim().length < 3} loading={freeze.isPending} icon={<Snowflake size={18} weight="fill" />} onClick={() => freeze.mutate(!u.frozen)}>
          {u.frozen ? 'Разморозить аккаунт' : 'Заморозить аккаунт'}
        </Slab>
      </Section>

      <Section title="Корректировка баланса">
        <Notice>Например, если TON пришёл без memo. Каждая корректировка пишется в журнал и в историю пользователя.</Notice>
        <Segmented
          label="Направление"
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'credit', label: 'Начислить' },
            { value: 'debit', label: 'Списать' },
          ]}
        />
        <AssetSwitch asset={asset} onPick={setAsset} onlyHeld={false} />
        <Field label="Сумма" htmlFor="adj-amount">
          <div className="input-group">
            <input id="adj-amount" className="input input--amount num" inputMode="decimal" placeholder="0" value={amount} onChange={(e) => setAmount(cleanAmountInput(e.target.value, getAsset(asset)!.decimals))} />
            <span className="input-group__unit">{asset}</span>
          </div>
        </Field>
        <Field label="Причина" htmlFor="adj-reason">
          <input id="adj-reason" className="input" value={reason} placeholder="Обязательно" onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Slab
          tone="danger"
          disabled={!amount || Number(amount) <= 0 || reason.trim().length < 3}
          loading={adjust.isPending}
          onClick={() => adjust.mutate(undefined, { onSuccess: () => { setAmount(''); setReason(''); } })}
        >
          {direction === 'credit' ? 'Начислить' : 'Списать'} {amount || '0'} {asset}
        </Slab>
      </Section>

      <Section title={t['history.title']}>
        {u.activity.length ? (
          <div className="rows">
            {u.activity.map((a) => (
              <ActivityRow key={a.id} item={a} readOnly />
            ))}
          </div>
        ) : (
          <p className="state">Операций нет.</p>
        )}
      </Section>
    </Screen>
  );
}

/* ---------- audit log ---------- */

const ACTION_LABEL: Record<string, string> = {
  'withdrawal.approve': 'Одобрен вывод',
  'withdrawal.reject': 'Отклонён вывод',
  'user.freeze': 'Заморожен пользователь',
  'user.unfreeze': 'Разморожен пользователь',
  'user.adjust': 'Корректировка баланса',
  'sweep.retry': 'Повтор сбора',
};

export function AdminLog() {
  const lang = useLang();
  const q = useAdmin<{ items: AdminActionDTO[] }>('/actions', ['actions']);
  return (
    <Screen className="admin">
      <ScreenHeader title="Журнал действий" />
      {q.isPending ? (
        <Skeleton w="100%" h={120} r={24} />
      ) : q.data?.items.length ? (
        <div className="rows">
          {q.data.items.map((a) => {
            const p = (a.payload ?? {}) as { reason?: string; amount?: string; asset?: string };
            return (
              <Row
                key={a.id}
                title={ACTION_LABEL[a.action] ?? a.action}
                subtitle={[p.amount && p.asset ? `${p.amount} ${p.asset}` : null, p.reason, `админ ${a.adminTgId}`].filter(Boolean).join(' · ')}
                wrapSub
                trailingSub={formatDateTime(a.createdAt, lang)}
                onClick={a.targetType === 'user' ? () => navigate(`/admin/users/${a.targetId}`) : undefined}
              />
            );
          })}
        </div>
      ) : (
        <p className="state">Действий пока не было.</p>
      )}
    </Screen>
  );
}
